import { useEffect, useMemo, useRef, useState } from "react";
import type { Facet } from "../api/types";

type Props = {
  /** Decides the colour: the control wears the colour of the chips it selects. */
  kind: "tags" | "styles" | "genres" | "labels" | "meters" | "keys";
  noneLabel: string;
  findLabel: string;
  options: Facet[];
  selected: string[];
  onChange: (next: string[]) => void;
};

/**
 * A dropdown of checkboxes, and every tick is a further restriction.
 *
 * A plain <select multiple> was the obvious markup and the wrong one: a
 * fixed-height box you ctrl-click is unusable at ten thousand tags. So this is
 * a button that opens a list you tick, with a search of its own to find one.
 *
 * There is deliberately no "select all matching": each pick restricts further,
 * so taking twenty at once would only ever return nothing.
 */
export function MultiSelect({
  kind, noneLabel, findLabel, options, selected, onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [find, setFind] = useState("");
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    // pointerdown, not mousedown: iOS turns a tap on plain text into no
    // mouse event at all, so a menu could only be closed from its button.
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const shown = useMemo(() => {
    const needle = find.trim().toLowerCase();
    const on = new Set(selected);
    return options.filter(
      // A tag already ticked stays visible however the list is narrowed, or it
      // would look as though it had been dropped.
      (o) => on.has(o.value) || !needle || o.label.toLowerCase().includes(needle),
    );
  }, [options, find, selected]);

  const label =
    selected.length === 0
      ? noneLabel
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label ?? selected[0]!)
        : `${selected.length} selected`;

  return (
    <div className={`ms ${kind}${open ? " open" : ""}${selected.length ? " on" : ""}`} ref={box}>
      <button type="button" className="ms-btn" onClick={() => setOpen((v) => !v)}>
        {label}
      </button>
      <div className="ms-menu">
        <div className="ms-tools">
          <input
            type="search"
            className="ms-find"
            placeholder={findLabel}
            value={find}
            onChange={(e) => setFind(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <button type="button" onClick={() => { onChange([]); setFind(""); }}>
            Clear
          </button>
        </div>
        <div className="ms-list">
          {shown.map((o) => (
            <label key={o.value}>
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() =>
                  onChange(
                    selected.includes(o.value)
                      ? selected.filter((v) => v !== o.value)
                      : [...selected, o.value],
                  )
                }
              />
              {o.label} ({o.count})
            </label>
          ))}
          {shown.length === 0 && (
            <div className="ms-empty">Nothing matches that.</div>
          )}
        </div>
      </div>
    </div>
  );
}
